import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Package, DollarSign, Box, FileText, Filter, Download } from 'lucide-react';
import "../../Styling/ProductionDashboard.css";

const ProductionDashboard = () => {
  const [filters, setFilters] = useState({
    orgId: '',
    companyCode: '',
    officeCode: '',
    storeCode: '',
    deptCode: ''
  });
  const [dropdownOptions, setDropdownOptions] = useState({
    companyCodes: [],
    officeCodes: [],
    storeCodes: [],
    deptCodes: []
  });
  const [names, setNames] = useState({
    orgName: '',
    companyName: '',
    officeName: '',
    storeName: '',
    deptName: ''
  });

  const [activeIndex, setActiveIndex] = useState({
    plant: null,
    itemMaster: null,
    sku: null
  });

  const [dashboardData, setDashboardData] = useState({
    totalProductOrderAmount: 0,
    totalProductAmount: 0,
    totalUnitsProduce: 0,
    openOrders: 0,
    prodByPlant: [],
    prodByItemMaster: [],
    prodBySku: []
  });

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const dashboardRef = useRef();

  // API endpoints configuration
  const API_ENDPOINTS = {
    getOrgName: '/api/organizations/{orgId}/name',
    getCompanyName: '/api/companies/{companyCode}/name',
    getOfficeName: '/api/offices/{officeCode}/name',
    getStoreName: '/api/stores/{storeCode}/name',
    getDeptName: '/api/departments/{deptCode}/name',
    getDropdownOptions: '/api/dropdown-options',
    getDashboardData: '/api/production-dashboard'
  };

  // Fetch dropdown options when component mounts
  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  // Fetch dashboard data when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [filters]);

  // Fetch name when orgId is selected
  useEffect(() => {
    if (filters.orgId) {
      fetchOrgName(filters.orgId);
    } else {
      setNames(prev => ({ ...prev, orgName: '' }));
    }
  }, [filters.orgId]);

  // Fetch company name when companyCode is selected
  useEffect(() => {
    if (filters.companyCode) {
      fetchCompanyName(filters.companyCode);
    } else {
      setNames(prev => ({ ...prev, companyName: '' }));
    }
  }, [filters.companyCode]);

  // Fetch office name when officeCode is selected
  useEffect(() => {
    if (filters.officeCode) {
      fetchOfficeName(filters.officeCode);
    } else {
      setNames(prev => ({ ...prev, officeName: '' }));
    }
  }, [filters.officeCode]);

  // Fetch store name when storeCode is selected
  useEffect(() => {
    if (filters.storeCode) {
      fetchStoreName(filters.storeCode);
    } else {
      setNames(prev => ({ ...prev, storeName: '' }));
    }
  }, [filters.storeCode]);

  // Fetch department name when deptCode is selected
  useEffect(() => {
    if (filters.deptCode) {
      fetchDeptName(filters.deptCode);
    } else {
      setNames(prev => ({ ...prev, deptName: '' }));
    }
  }, [filters.deptCode]);

  const fetchDropdownOptions = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.getDropdownOptions);
      const data = await response.json();
      setDropdownOptions(data);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
      // Fallback to sample data
      setDropdownOptions({
        companyCodes: ['CC001', 'CC002', 'CC003', 'CC004'],
        officeCodes: ['OFF01', 'OFF02', 'OFF03', 'OFF04'],
        storeCodes: ['ST001', 'ST002', 'ST003', 'ST004'],
        deptCodes: ['DEPT01', 'DEPT02', 'DEPT03', 'DEPT04']
      });
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`${API_ENDPOINTS.getDashboardData}?${queryParams}`);
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fallback to sample data in rupees
      setDashboardData({
        totalProductOrderAmount: 28500000, // 2.85 Crore
        totalProductAmount: 26500000, // 2.65 Crore
        totalUnitsProduce: 15420,
        openOrders: 127,
        prodByPlant: [
          { plant: 'Plant A', units: 5200 },
          { plant: 'Plant B', units: 4100 },
          { plant: 'Plant C', units: 3500 },
          { plant: 'Plant D', units: 2620 }
        ],
        prodByItemMaster: [
          { itemMaster: 'ITEM-M001', units: 4800 },
          { itemMaster: 'ITEM-M002', units: 3900 },
          { itemMaster: 'ITEM-M003', units: 3200 },
          { itemMaster: 'ITEM-M004', units: 2100 },
          { itemMaster: 'ITEM-M005', units: 1420 }
        ],
        prodBySku: [
          { sku: 'SKU-P123', units: 5100 },
          { sku: 'SKU-P456', units: 4200 },
          { sku: 'SKU-P789', units: 3500 },
          { sku: 'SKU-P012', units: 2620 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgName = async (orgId) => {
    try {
      const response = await fetch(API_ENDPOINTS.getOrgName.replace('{orgId}', orgId));
      const data = await response.json();
      setNames(prev => ({ ...prev, orgName: data.name }));
    } catch (error) {
      console.error('Error fetching organization name:', error);
      setNames(prev => ({ ...prev, orgName: 'Organization Name' }));
    }
  };

  const fetchCompanyName = async (companyCode) => {
    try {
      const response = await fetch(API_ENDPOINTS.getCompanyName.replace('{companyCode}', companyCode));
      const data = await response.json();
      setNames(prev => ({ ...prev, companyName: data.name }));
    } catch (error) {
      console.error('Error fetching company name:', error);
      setNames(prev => ({ ...prev, companyName: 'Company Name' }));
    }
  };

  const fetchOfficeName = async (officeCode) => {
    try {
      const response = await fetch(API_ENDPOINTS.getOfficeName.replace('{officeCode}', officeCode));
      const data = await response.json();
      setNames(prev => ({ ...prev, officeName: data.name }));
    } catch (error) {
      console.error('Error fetching office name:', error);
      setNames(prev => ({ ...prev, officeName: 'Office Name' }));
    }
  };

  const fetchStoreName = async (storeCode) => {
    try {
      const response = await fetch(API_ENDPOINTS.getStoreName.replace('{storeCode}', storeCode));
      const data = await response.json();
      setNames(prev => ({ ...prev, storeName: data.name }));
    } catch (error) {
      console.error('Error fetching store name:', error);
      setNames(prev => ({ ...prev, storeName: 'Store Name' }));
    }
  };

  const fetchDeptName = async (deptCode) => {
    try {
      const response = await fetch(API_ENDPOINTS.getDeptName.replace('{deptCode}', deptCode));
      const data = await response.json();
      setNames(prev => ({ ...prev, deptName: data.name }));
    } catch (error) {
      console.error('Error fetching department name:', error);
      setNames(prev => ({ ...prev, deptName: 'Department Name' }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    
    // Reset dependent filters when parent filter changes
    if (field === 'orgId') {
      setFilters(prev => ({ 
        ...prev, 
        companyCode: '', 
        officeCode: '', 
        storeCode: '', 
        deptCode: '' 
      }));
    } else if (field === 'companyCode') {
      setFilters(prev => ({ 
        ...prev, 
        officeCode: '', 
        storeCode: '', 
        deptCode: '' 
      }));
    } else if (field === 'officeCode') {
      setFilters(prev => ({ 
        ...prev, 
        storeCode: '', 
        deptCode: '' 
      }));
    } else if (field === 'storeCode') {
      setFilters(prev => ({ 
        ...prev, 
        deptCode: '' 
      }));
    }
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      // Simulate PDF generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = '#';
      link.download = 'production-dashboard-report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('PDF download triggered');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  // Format currency in Indian Rupees (₹) with lakhs/crores
  const formatCurrency = (value) => {
    if (value >= 10000000) { // 1 Crore = 100 lakhs = 10,000,000
      const crores = value / 10000000;
      return `₹${crores.toFixed(2)} Cr`;
    } else if (value >= 100000) { // 1 Lakh = 100,000
      const lakhs = value / 100000;
      return `₹${lakhs.toFixed(2)} L`;
    } else if (value >= 1000) { // Thousands
      const thousands = value / 1000;
      return `₹${thousands.toFixed(2)} K`;
    } else {
      return `₹${value}`;
    }
  };

  // Format numbers with Indian numbering system
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const ProductCustomBar = (props) => {
    const { fill, x, y, width, height, index, chartType } = props;
    const isActive = activeIndex[chartType] === index;
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          opacity={isActive ? 1 : 0.8}
          className="product-custom-bar"
          onMouseEnter={() => setActiveIndex(prev => ({ ...prev, [chartType]: index }))}
          onMouseLeave={() => setActiveIndex(prev => ({ ...prev, [chartType]: null }))}
        />
        {isActive && (
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="none"
            stroke="#fff"
            strokeWidth={2}
            opacity={0.6}
          />
        )}
      </g>
    );
  };

  const ProductCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="product-custom-tooltip">
          <p className="product-tooltip-label">{label}</p>
          <p className="product-tooltip-value">
            {formatNumber(payload[0].value)} units
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="product-dashboard" ref={dashboardRef}>
      {/* PDF Loading Overlay */}
      {pdfLoading && (
        <div className="product-pdf-loading">
          <div className="product-pdf-spinner"></div>
          <span>Generating PDF Report...</span>
        </div>
      )}
      
      <div className="product-dashboard-container">
        <div className="product-dashboard-header">
          <h1 className="product-dashboard-title">Production Dashboard</h1>
          <div className="product-filter-indicator">
            <Filter className="product-filter-icon" />
            <span className="product-filter-text">Filters Active</span>
          </div>
        </div>
        
        {/* Filters - All in one line */}
        <div className="product-filters-container">
          <div className="product-filters-grid">
            <div className="product-filter-group">
              <label className="product-filter-label">Org ID</label>
              <input
                type="text"
                value={filters.orgId}
                onChange={(e) => handleFilterChange('orgId', e.target.value)}
                className="product-filter-input"
                placeholder="Enter Org ID"
              />
              {names.orgName && (
                <div className="product-filter-name">
                  {names.orgName}
                </div>
              )}
            </div>
            
            <div className="product-filter-group">
              <label className="product-filter-label">Company Code</label>
              <input
                type="text"
                value={filters.companyCode}
                onChange={(e) => handleFilterChange('companyCode', e.target.value)}
                className="product-filter-input"
                placeholder="Enter Company Code"
                disabled={!filters.orgId}
              />
              {names.companyName && (
                <div className="product-filter-name">
                  {names.companyName}
                </div>
              )}
            </div>
            
            <div className="product-filter-group">
              <label className="product-filter-label">Office Code</label>
              <select
                value={filters.officeCode}
                onChange={(e) => handleFilterChange('officeCode', e.target.value)}
                className="product-filter-select"
                disabled={!filters.companyCode}
              >
                <option value="">Select Office</option>
                {dropdownOptions.officeCodes.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              {names.officeName && (
                <div className="product-filter-name">
                  {names.officeName}
                </div>
              )}
            </div>
            
            <div className="product-filter-group">
              <label className="product-filter-label">Store Code</label>
              <select
                value={filters.storeCode}
                onChange={(e) => handleFilterChange('storeCode', e.target.value)}
                className="product-filter-select"
                disabled={!filters.officeCode}
              >
                <option value="">Select Store</option>
                {dropdownOptions.storeCodes.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              {names.storeName && (
                <div className="product-filter-name">
                  {names.storeName}
                </div>
              )}
            </div>
            
            <div className="product-filter-group">
              <label className="product-filter-label">Dept Code</label>
              <select
                value={filters.deptCode}
                onChange={(e) => handleFilterChange('deptCode', e.target.value)}
                className="product-filter-select"
                disabled={!filters.storeCode}
              >
                <option value="">Select Dept</option>
                {dropdownOptions.deptCodes.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              {names.deptName && (
                <div className="product-filter-name">
                  {names.deptName}
                </div>
              )}
            </div>

            {/* PDF Export Button */}
            <div className="product-filter-group product-export-group">
              <label className="product-filter-label">&nbsp;</label>
              <button
                onClick={handleDownloadPDF}
                disabled={pdfLoading || loading}
                className="product-btn-export"
              >
                {pdfLoading ? (
                  <>
                    <div className="product-export-spinner"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="product-export-icon" />
                    Export to PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="product-stats-grid">
          <div className="product-stat-card product-stat-total">
            <div className="product-stat-content">
              <div className="product-stat-text">
                <div className="product-stat-title">Total Product Order Amount</div>
                <div className="product-stat-value">{formatCurrency(dashboardData.totalProductOrderAmount)}</div>
              </div>
              <div className="product-stat-icon">
                <DollarSign className="product-icon" />
              </div>
            </div>
          </div>
          
          <div className="product-stat-card product-stat-receipt">
            <div className="product-stat-content">
              <div className="product-stat-text">
                <div className="product-stat-title">Total Product Amount</div>
                <div className="product-stat-value">{formatCurrency(dashboardData.totalProductAmount)}</div>
              </div>
              <div className="product-stat-icon">
                <Package className="product-icon" />
              </div>
            </div>
          </div>
          
          <div className="product-stat-card product-stat-invoice">
            <div className="product-stat-content">
              <div className="product-stat-text">
                <div className="product-stat-title">Total Units Produce</div>
                <div className="product-stat-value">{formatNumber(dashboardData.totalUnitsProduce)}</div>
              </div>
              <div className="product-stat-icon">
                <Box className="product-icon" />
              </div>
            </div>
          </div>
          
          <div className="product-stat-card product-stat-payment">
            <div className="product-stat-content">
              <div className="product-stat-text">
                <div className="product-stat-title">Open Orders</div>
                <div className="product-stat-value">{formatNumber(dashboardData.openOrders)}</div>
              </div>
              <div className="product-stat-icon">
                <FileText className="product-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Bar Charts */}
        <div className="product-charts-grid">
          {/* Production by Plant */}
          <div className="product-chart-container">
            <h2 className="product-chart-title">
              <span className="product-chart-accent product-chart-accent-blue"></span>
              Production by Plant
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dashboardData.prodByPlant} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{fontSize: 11}} />
                <YAxis dataKey="plant" type="category" width={80} tick={{fontSize: 11}} />
                <Tooltip content={<ProductCustomTooltip />} cursor={{fill: 'rgba(59, 130, 246, 0.1)'}} />
                <Bar 
                  dataKey="units" 
                  fill="url(#blueGradient)" 
                  radius={[0, 8, 8, 0]}
                  shape={(props) => <ProductCustomBar {...props} chartType="plant" />}
                  animationDuration={1000}
                  animationBegin={0}
                />
                <defs>
                  <linearGradient id="blueGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#60a5fa" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Production by Item Master */}
          <div className="product-chart-container">
            <h2 className="product-chart-title">
              <span className="product-chart-accent product-chart-accent-green"></span>
              Production by Item Master
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dashboardData.prodByItemMaster} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{fontSize: 11}} />
                <YAxis dataKey="itemMaster" type="category" width={80} tick={{fontSize: 11}} />
                <Tooltip content={<ProductCustomTooltip />} cursor={{fill: 'rgba(16, 185, 129, 0.1)'}} />
                <Bar 
                  dataKey="units" 
                  fill="url(#greenGradient)" 
                  radius={[0, 8, 8, 0]}
                  shape={(props) => <ProductCustomBar {...props} chartType="itemMaster" />}
                  animationDuration={1000}
                  animationBegin={200}
                />
                <defs>
                  <linearGradient id="greenGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#34d399" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Production by SKU */}
          <div className="product-chart-container">
            <h2 className="product-chart-title">
              <span className="product-chart-accent product-chart-accent-purple"></span>
              Production by SKU
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dashboardData.prodBySku} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{fontSize: 11}} />
                <YAxis dataKey="sku" type="category" width={80} tick={{fontSize: 11}} />
                <Tooltip content={<ProductCustomTooltip />} cursor={{fill: 'rgba(139, 92, 246, 0.1)'}} />
                <Bar 
                  dataKey="units" 
                  fill="url(#purpleGradient)" 
                  radius={[0, 8, 8, 0]}
                  shape={(props) => <ProductCustomBar {...props} chartType="sku" />}
                  animationDuration={1000}
                  animationBegin={400}
                />
                <defs>
                  <linearGradient id="purpleGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#a78bfa" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionDashboard;